/*
  AMOR BY YOU (update 2, chapter 8; stage א5): the request field's tools, the
  connection to the AI server component, and the answer card. Loaded on every
  page (theme.liquid).

  Tools: the microphone is a concept and shows "coming soon"; the camera
  attaches one image, shrunk in the browser to at most 1,024px wide
  (architecture, "מהלך של בקשה אחת"). Sending from another page carries the
  image to the AMOR BY YOU page through DemoStore, which removes it on read.

  window.AmorAI: the only door to the server component. Its address is the
  general setting "AMOR BY YOU server" (body[data-ai-url]); the contract is
  "הודעה למסלול א - רכיב השרת", and for the gift note "הודעה למסלול א -
  ברכה" (assets/greeting.js). The browser gives up after 25 seconds. A
  signed-in visitor's profile goes with every request, from her editable
  taste and sizes; a guest sends none. The server answers with handles and
  colors only: every product is loaded from the store (sections/edit-item).

  <amor-answer>: one request, one answer with an action button, not a chat.
  The answer is kept in DemoStore and its id goes into the address (?r=), so
  a refresh or going back shows it again without asking the model. A look
  for an occasion, an image or a gift (intents 1 to 3) becomes a Your Private
  Edit, and the page moves to it (/pages/private-edit?id=).
*/
(() => {
  const root = window.Shopify?.routes?.root || '/';
  const MAX_WIDTH = 1024;
  const TIMEOUT_MS = 25000;
  const TEXT_MAX = 300;
  const CHOOSING_AFTER_MS = 3000;
  const EDIT_INTENTS = ['occasion_look', 'image_inspiration', 'gift'];
  const MODES = { occasion_look: 'occasion', image_inspiration: 'image', gift: 'gift' };

  const store = () => window.DemoStore;
  const normalize = (text) => String(text || '').replace(/\s+/g, ' ').trim();

  /* -------------------------------------------------------------------------
     Field tools
  ------------------------------------------------------------------------- */
  const formImage = new WeakMap();

  const notifyChange = (form) => form.dispatchEvent(new CustomEvent('request:change', { bubbles: true }));

  const setImage = (form, dataUrl) => {
    const chip = form.querySelector('[data-request-chip]');
    const thumb = form.querySelector('[data-request-thumb]');
    if (!chip || !thumb) return;
    if (dataUrl) {
      formImage.set(form, dataUrl);
      thumb.src = dataUrl;
      chip.hidden = false;
    } else {
      formImage.delete(form);
      thumb.removeAttribute('src');
      chip.hidden = true;
    }
    notifyChange(form);
  };

  const drawScaled = (src, scaleFor, quality) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const scale = scaleFor(img);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => reject(new Error('unreadable image'));
      img.src = src;
    });

  const shrink = async (file) => {
    if (!file.type.startsWith('image/')) throw new Error('not an image');
    const url = URL.createObjectURL(file);
    try {
      return await drawScaled(url, (img) => Math.min(1, MAX_WIDTH / img.naturalWidth), 0.8);
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  /** A small copy of an attached image, shown beside the request (spec chapter 27). */
  const thumbnail = (dataUrl, size = 160) =>
    drawScaled(dataUrl, (img) => Math.min(1, size / Math.max(img.naturalWidth, img.naturalHeight)), 0.7).catch(() => null);

  const showFieldMessage = (form, text) => {
    const message = form.querySelector('[data-request-message]');
    if (!message) return;
    message.textContent = text || '';
    message.hidden = !text;
  };

  document.addEventListener('click', (event) => {
    const mic = event.target.closest('[data-request-mic]');
    if (mic) {
      const form = mic.closest('[data-request-form]');
      const bubble = form?.querySelector('[data-request-bubble]');
      if (!bubble) return;
      bubble.hidden = false;
      window.clearTimeout(bubble.timer);
      bubble.timer = window.setTimeout(() => {
        bubble.hidden = true;
      }, 3000);
      return;
    }
    const attach = event.target.closest('[data-request-attach]');
    if (attach) {
      attach.closest('[data-request-form]')?.querySelector('[data-request-file]')?.click();
      return;
    }
    const remove = event.target.closest('[data-request-remove]');
    if (remove) {
      const form = remove.closest('[data-request-form]');
      setImage(form, null);
      form.querySelector('input[name="q"]')?.focus();
    }
  });

  document.addEventListener('change', async (event) => {
    const input = event.target.closest?.('[data-request-file]');
    if (!input) return;
    const form = input.closest('[data-request-form]');
    const file = input.files?.[0];
    input.value = '';
    if (!file || !form) return;
    try {
      setImage(form, await shrink(file));
      showFieldMessage(form, null);
    } catch {
      showFieldMessage(form, form.dataset.messageImage);
    }
  });

  // Sending from the home banner: the image crosses to the AMOR BY YOU page.
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-request-form]');
    if (!form || event.defaultPrevented || form.closest('amor-answer, .amor-ask')) return;
    const dataUrl = formImage.get(form);
    if (!dataUrl) return;
    store()?.setRequestImage(dataUrl);
    let flag = form.querySelector('input[name="image"]');
    if (!flag) {
      flag = document.createElement('input');
      flag.type = 'hidden';
      flag.name = 'image';
      form.append(flag);
    }
    flag.value = '1';
  });

  /* -------------------------------------------------------------------------
     window.AmorAI: the server component
  ------------------------------------------------------------------------- */
  class AiError extends Error {
    constructor(code) {
      super(code);
      this.code = code;
    }
  }

  const serverUrl = () => (document.body?.dataset.aiUrl || '').trim().replace(/\/+$/, '');

  /** { media_type, data } from a data URL, without the "data:...;base64," prefix. */
  const imagePayload = (dataUrl) => {
    const match = /^data:(image\/[a-z+.-]+);base64,(.+)$/i.exec(dataUrl || '');
    return match ? { media_type: match[1].toLowerCase(), data: match[2] } : null;
  };

  const seed = () => {
    try {
      return JSON.parse(document.getElementById('DemoCustomer')?.textContent || 'null');
    } catch {
      return null;
    }
  };

  /**
   * The signed-in visitor's profile, as the server reads it: the fields of the
   * demo_customer metaobject and the world. Brands, colors, styles and sizes
   * come from her editable profile, so editing her taste changes the choice.
   */
  const customer = () => {
    const data = seed();
    if (!store()?.signedIn() || !data) return null;
    const profile = store().profile() || data.profile || {};
    const name = (store().details().name || data.name || '').trim().split(/\s+/)[0] || data.firstName || '';
    return {
      first_name: name,
      world: 'women',
      brands: profile.brands || [],
      colors: profile.colors || [],
      styles: profile.styles || [],
      sizes: profile.sizes || [],
      wardrobe: data.wardrobe || [],
      orders: data.orders || [],
    };
  };

  const post = async (path, body) => {
    const url = serverUrl();
    if (!url) throw new AiError('network');
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(`${url}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }
      if (!response.ok || !data) throw new AiError(data?.error?.code || (response.ok ? 'model_error' : `http_${response.status}`));
      return data;
    } catch (error) {
      if (error instanceof AiError) throw error;
      throw new AiError(error?.name === 'AbortError' ? 'timeout' : 'network');
    } finally {
      window.clearTimeout(timer);
    }
  };

  /** Leaves out empty fields; the server treats a missing field as its default. */
  const compact = (body) =>
    Object.fromEntries(Object.entries(body).filter(([, value]) => value !== null && value !== undefined && value !== ''));

  const ask = (text, image) =>
    post('/ask', { text: normalize(text).slice(0, TEXT_MAX), image: imagePayload(image), customer: customer() });

  const edit = (body) => post('/edit', { ...compact(body), customer: customer() });

  const look = (body) => post('/look', { ...compact(body), customer: customer() });

  /**
   * The gift note in the cart (update 3, "הודעה למסלול א - ברכה"): the whole
   * conversation every time, the bag's items (title, vendor, type; never a
   * price), and for a signed-in visitor her first name only, as it is now.
   */
  const greeting = (messages, items) =>
    post('/greeting', { messages, items, first_name: customer()?.first_name || null });

  /** Errors after which "try again" makes sense (the contract's error table). */
  const retryable = (code) => ['timeout', 'model_error', 'network'].includes(code) || /^http_5/.test(code);

  /* --- Products: from the store, by handle (sections/edit-item.liquid) --------- */
  const productCache = new Map();

  const loadProduct = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`${root}products/${encodeURIComponent(handle)}?section_id=edit-item`)
          .then((res) => (res.ok ? res.text() : ''))
          .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('[data-edit-product]'))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };

  const fromTemplate = (node, name) => {
    const template = node.querySelector(`template[${name}]`);
    return template ? document.importNode(template.content, true).firstElementChild : null;
  };

  /**
   * One item of an answer ({ handle, color, reason, owned }) as the store has
   * it now. Null when the product is gone, or sold out in that color since the
   * server's catalog was built, unless she already owns it (architecture,
   * "מהלך של בקשה אחת", step 5).
   */
  const loadItem = async (entry) => {
    const product = await loadProduct(entry?.handle);
    if (!product) return null;
    const colors = [...product.querySelectorAll('[data-edit-color]')];
    let node = colors.find((item) => item.dataset.editColor === (entry.color || ''));
    if (!node) node = colors.find((item) => item.hasAttribute('data-available')) || colors[0];
    if (!node) return null;
    const available = node.hasAttribute('data-available') && !product.hasAttribute('data-store-only');
    if (!available && !entry.owned) return null;
    const row = node.querySelector('template[data-edit-row]')?.content.querySelector('[data-look-row]');
    const image = node.querySelector('template[data-edit-card]')?.content.querySelector('.card__image');
    // The item for the avatar (stage א8): its spot on the figure, its world and the variant.
    let avatar = null;
    try {
      avatar = JSON.parse(node.querySelector('[data-avatar-item]')?.textContent || 'null');
    } catch {
      avatar = null;
    }
    return {
      ...entry,
      handle: product.dataset.handle,
      title: product.dataset.title,
      vendor: product.dataset.vendor,
      type: product.dataset.type,
      world: product.dataset.world,
      url: product.dataset.url,
      color: node.dataset.editColor || null,
      available,
      price: Number(row?.dataset.price || 0),
      image: image?.getAttribute('src') || '',
      imageSrcset: image?.getAttribute('srcset') || '',
      avatar,
      card: () => fromTemplate(node, 'data-edit-card'),
      row: () => fromTemplate(node, 'data-edit-row'),
    };
  };

  const formatMoney = (cents, format) => {
    const amount = cents / 100;
    const text = Number.isInteger(amount)
      ? amount.toLocaleString('en-US')
      : amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return String(format || '[amount]').replace('[amount]', text);
  };

  /**
   * The answer's text, with every address of a page on the site turned into
   * a link with the page's Hebrew name ("בקשה למסלול ב - כתובות בתשובות").
   * The text comes from a model, so this stays after the server is fixed.
   */
  const PATH = /(?:https?:\/\/[^\s/]+)?\/(?:pages|collections|products|blogs)\/[\w\-/%=&?.]+/g;
  const linkify = (text, names = {}, fallback = '') => {
    const fragment = document.createDocumentFragment();
    const value = String(text || '');
    let last = 0;
    for (const match of value.matchAll(PATH)) {
      let path = match[0].replace(/\.+$/, '');
      if (/^https?:/i.test(path)) {
        try {
          const url = new URL(path);
          if (url.origin !== window.location.origin) continue;
          path = url.pathname + url.search;
        } catch {
          continue;
        }
      }
      fragment.append(value.slice(last, match.index));
      const link = document.createElement('a');
      link.className = 'link';
      link.href = path;
      link.textContent = names[path.split('?')[0].replace(/\/$/, '')] || fallback || path;
      fragment.append(link);
      last = match.index + match[0].replace(/\.+$/, '').length;
    }
    fragment.append(value.slice(last));
    return fragment;
  };

  /** "d.m.yyyy" for every time[data-days] inside, from days relative to today. */
  const fillDates = (scope) => {
    if (!store()) return;
    scope.querySelectorAll('time[data-days]').forEach((element) => {
      const date = store().dateFromDays(element.dataset.days);
      element.dateTime = store().isoDate(date);
      element.textContent = `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
    });
  };

  /** An answer's Edit becomes a Your Private Edit in the demo layer; returns its id. */
  const createEdit = async ({ text, image, params, result }) => {
    const record = store().addEdit({
      text: normalize(text),
      mode: params?.mode || null,
      budget: params?.budget || 0,
      world: params?.world || null,
      image: Boolean(image),
      versions: [result],
      current: 0,
    });
    if (image) {
      const small = await thumbnail(image);
      if (small) store().setThumb(record.id, small);
    }
    return record.id;
  };

  const editUrl = (id) => `${root}pages/private-edit?id=${encodeURIComponent(id)}`;

  /** A signed-in visitor's size is chosen in a size sheet row: her size in the item's size system (spec chapter 27). */
  const presetSize = (row) => {
    if (!store()?.signedIn()) return;
    const pill = [...row.querySelectorAll('[data-look-size]:not([disabled])')].find((button) => {
      const size = button.dataset.size || '';
      return size && store().sizeFor(size.split(' ')[0]) === size;
    });
    if (!pill) return;
    pill.setAttribute('aria-pressed', 'true');
    row.dataset.variantId = pill.dataset.lookSize;
  };

  /**
   * "Add the look to the bag": the same size sheet as Buy the Look (spec
   * chapter 29), with a row for each loaded item. The empty form is the
   * template in snippets/build-look-drawer.liquid, on every page.
   */
  const openSizeSheet = (items, trigger) => {
    const drawer = document.getElementById('LookSheet');
    const template = document.querySelector('template[data-look-sheet-form]');
    if (!drawer || !template) return;
    const form = document.importNode(template.content, true).querySelector('amor-look-form');
    const list = form.querySelector('.look-form__list');
    items.forEach((item) => {
      const row = item.row?.();
      if (!row) return;
      presetSize(row);
      list.append(row);
    });
    drawer.querySelector('[data-look-sheet-body]').replaceChildren(form);
    drawer.open(trigger);
  };

  /** The fitting drawer's link for these items, each in its color. */
  const fittingUrl = (items) =>
    `/pages/fitting?products=${encodeURIComponent(items.map((item) => (item.color ? `${item.handle}:${item.color}` : item.handle)).join(','))}`;

  window.AmorAI = {
    ask,
    edit,
    look,
    greeting,
    customer,
    retryable,
    imagePayload,
    thumbnail,
    loadProduct,
    loadItem,
    formatMoney,
    linkify,
    fillDates,
    createEdit,
    editUrl,
    openSizeSheet,
    fittingUrl,
  };

  /* -------------------------------------------------------------------------
     <amor-answer>
  ------------------------------------------------------------------------- */
  class AmorAnswer extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('.amor-ask')?.querySelector('[data-request-form]');
      this.input = this.form?.querySelector('input[name="q"]');
      this.card = this.querySelector('[data-answer-card]');
      this.body = this.querySelector('[data-answer-body]');
      if (!this.form || !this.input) return;
      try {
        this.strings = JSON.parse(this.querySelector('[data-answer-strings]')?.textContent || '{}');
      } catch {
        this.strings = {};
      }
      this.token = 0;

      this.form.addEventListener('submit', (event) => {
        // An empty request is left to components.js, which stops it and says why.
        if (!normalize(this.input.value) && !formImage.get(this.form)) return;
        event.preventDefault();
        this.send(this.input.value, formImage.get(this.form) || null);
      });
      this.addEventListener('click', (event) => this.onClick(event));
      document.addEventListener('click', (event) => {
        const example = event.target.closest('[data-answer-example]');
        if (!example) return;
        this.input.value = example.dataset.answerExample;
        this.form.requestSubmit();
      });
      window.addEventListener('popstate', () => this.fromAddress(false));
      this.fromAddress(true);
    }

    onClick(event) {
      if (event.target.closest('[data-answer-new]')) {
        this.reset();
        return;
      }
      if (event.target.closest('[data-answer-retry]')) {
        this.retry?.();
        return;
      }
      const option = event.target.closest('[data-answer-option]');
      if (option) {
        this.clarify(option.dataset.answerOption);
        return;
      }
      const fill = event.target.closest('[data-answer-fill]');
      if (fill) {
        this.input.value = fill.textContent.trim();
        this.form.requestSubmit();
      }
    }

    /**
     * The address: ?r= a kept answer (shown again, not sent); ?q= a request to
     * send, from another page's field or a link; ?image=1 an image from the
     * home banner; ?draft= text to put in the field without sending ("edit
     * the request" on Your Private Edit).
     */
    fromAddress(first) {
      const params = new URLSearchParams(window.location.search);
      const draft = params.get('draft');
      if (first && draft !== null) {
        this.input.value = draft;
        this.hide();
        window.requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
        return;
      }
      const saved = params.get('r') ? store()?.answer(params.get('r')) : null;
      if (saved) {
        this.input.value = saved.text || '';
        this.request = { text: saved.text || '', image: null };
        this.answer = saved;
        this.token += 1;
        this.showRequest({ text: saved.text, image: saved.image ? store().thumb(saved.id) : null, hadImage: saved.image });
        this.show(saved, false);
        return;
      }
      const text = params.get('q') || '';
      const image = first && params.get('image') ? store()?.takeRequestImage() : null;
      if (!normalize(text) && !image) {
        this.hide();
        return;
      }
      this.input.value = text;
      this.run({ text, image });
    }

    send(text, image) {
      const url = new URL(window.location.href);
      // ?reset=1 would clear the kept answer on a refresh, and the request would go again.
      ['state', 'image', 'r', 'draft', 'reset'].forEach((key) => url.searchParams.delete(key));
      if (normalize(text)) url.searchParams.set('q', normalize(text));
      else url.searchParams.delete('q');
      window.history.pushState({}, '', url);
      setImage(this.form, null);
      this.run({ text, image });
    }

    async run(request) {
      this.request = request;
      this.retry = () => this.run(request);
      const token = ++this.token;
      this.showRequest(request);
      this.renderLoading();
      this.hidden = false;
      this.reveal();
      let data;
      try {
        data = await window.AmorAI.ask(request.text, request.image);
      } catch (error) {
        if (token === this.token) this.renderError(error.code);
        return;
      }
      if (token !== this.token) return;
      const saved = store().addAnswer({ text: normalize(request.text), image: Boolean(request.image), data });
      if (request.image) {
        const thumb = await thumbnail(request.image, 96);
        if (thumb) store().setThumb(saved.id, thumb);
      }
      this.answer = saved;
      const url = new URL(window.location.href);
      url.searchParams.delete('image');
      url.searchParams.delete('reset');
      url.searchParams.set('r', saved.id);
      window.history.replaceState({}, '', url);
      this.show(saved, true);
    }

    show(answer, fresh) {
      const { data } = answer;
      this.hidden = false;
      if (EDIT_INTENTS.includes(data?.intent) && data.edit) this.showEdit(answer, fresh);
      else this.renderReply(data);
      this.reveal();
    }

    /** Intents 1 to 3: a Your Private Edit, a question first, or one of its other states. */
    async showEdit(answer, fresh) {
      const { data } = answer;
      const result = data.edit;
      const params = { ...(data.params || {}), mode: data.params?.mode || MODES[data.intent] };
      if (result.status === 'ok' || result.status === 'no_match') {
        let editId = answer.editId && store().edit(answer.editId) ? answer.editId : null;
        if (!editId) {
          editId = await window.AmorAI.createEdit({ text: answer.text, image: this.request?.image, params, result });
          store().updateAnswer(answer.id, { editId });
          this.answer = { ...answer, editId };
        }
        if (fresh) {
          window.location.assign(editUrl(editId));
          return;
        }
        this.renderReply({ reply: result.opening }, 'edit');
        const button = this.body.querySelector('[data-answer-edit-link]');
        if (button) button.href = editUrl(editId);
        return;
      }
      if (result.status === 'clarify' && result.question) {
        this.renderClarify(result);
        return;
      }
      if (result.status === 'bad_image') {
        this.renderState('bad_image');
        return;
      }
      this.renderReply({ intent: 'unrelated', reply: result.opening });
    }

    /** The answer to the question ("women or men?"): the same request, sent to /edit with it. */
    async clarify(option) {
      const answer = this.answer;
      if (!answer) return;
      const params = { ...(answer.data.params || {}), mode: answer.data.params?.mode || MODES[answer.data.intent] };
      const world = /גבר/.test(option) ? 'men' : /נש/.test(option) ? 'women' : params.world || null;
      const image = this.request?.image || null;
      this.retry = () => this.clarify(option);
      const token = ++this.token;
      this.renderLoading(true);
      let result;
      try {
        // Without the image (a refresh lost it), the request goes on in words.
        const text = answer.text || (answer.image && !image ? this.strings.imageFallback : '');
        result = await window.AmorAI.edit({
          text,
          image: window.AmorAI.imagePayload(image),
          mode: image ? params.mode : params.mode === 'image' ? 'occasion' : params.mode,
          budget: params.budget || 0,
          world,
          answer: option,
        });
      } catch (error) {
        if (token === this.token) this.renderError(error.code);
        return;
      }
      if (token !== this.token) return;
      const data = { ...answer.data, edit: result, params: { ...params, world } };
      store().updateAnswer(answer.id, { data, editId: null });
      this.answer = { ...answer, data, editId: null };
      this.showEdit(this.answer, true);
    }

    showRequest({ text, image, hadImage }) {
      const line = this.querySelector('[data-answer-request]');
      const textEl = this.querySelector('[data-answer-text]');
      const thumb = this.querySelector('[data-answer-thumb]');
      if (textEl) textEl.textContent = normalize(text) ? `«${normalize(text)}»` : this.strings.imageOnly || '';
      if (thumb) {
        thumb.hidden = !image;
        if (image) thumb.src = image;
        else thumb.removeAttribute('src');
      }
      if (line) line.hidden = !normalize(text) && !image && !hadImage;
    }

    template(name) {
      const template = this.querySelector(`template[data-answer-state="${name}"]`);
      return template ? template.content.cloneNode(true) : document.createDocumentFragment();
    }

    place(content, state) {
      window.clearTimeout(this.choosingTimer);
      window.AmorAI.fillDates(content);
      this.body.replaceChildren(content);
      this.card.dataset.state = state;
      delete this.card.dataset.error;
    }

    renderLoading(choosing) {
      const content = this.template('loading');
      const status = content.querySelector('[data-answer-status]');
      this.place(content, 'loading');
      if (choosing && status) status.textContent = this.strings.choosing;
      // An Edit takes longer than a short answer: after a moment, say what is happening.
      else if (status) this.choosingTimer = window.setTimeout(() => (status.textContent = this.strings.choosing), CHOOSING_AFTER_MS);
    }

    renderState(name) {
      this.place(this.template(name), name);
    }

    /**
     * "Something went wrong": try again where it makes sense, Women and Men when
     * the site is out of requests. A visitor over her own limit is told to wait
     * a few minutes, with Women and Men and no "try again" (stage א7).
     */
    renderError(code) {
      const content = this.template('error');
      const text = content.querySelector('[data-answer-error-text]');
      const retry = content.querySelector('[data-answer-retry]');
      const worlds = content.querySelector('[data-answer-worlds]');
      if (text && code === 'rate_limited') text.textContent = text.dataset.rateLimited;
      if (retry) retry.hidden = !window.AmorAI.retryable(code);
      if (worlds) worlds.hidden = !['site_limit', 'rate_limited'].includes(code);
      this.place(content, 'error');
      this.card.dataset.error = code;
    }

    renderClarify(result) {
      const content = this.template('clarify');
      const opening = content.querySelector('[data-answer-reply]');
      const question = content.querySelector('[data-answer-question]');
      const options = content.querySelector('[data-answer-options]');
      if (opening) opening.textContent = result.opening || '';
      if (question) question.textContent = result.question.text || '';
      (result.question.options || []).forEach((text) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'pill';
        pill.dataset.answerOption = text;
        pill.textContent = text;
        options?.append(pill);
      });
      this.place(content, 'clarify');
    }

    /**
     * The sentence and the action. An intent with its own part (a return, an
     * order, the boutique, a person, the club, sizes, unrelated) shows that
     * part; any other shows the server's button, and its second link.
     */
    renderReply(data, partName) {
      const content = this.template('reply');
      const reply = content.querySelector('[data-answer-reply]');
      const actions = content.querySelector('[data-answer-actions]');
      reply?.append(window.AmorAI.linkify(data?.reply, this.strings.pages, this.strings.pageFallback));
      const part = this.querySelector(`template[data-answer-part="${partName || data?.intent}"]`);
      if (part) {
        actions?.replaceWith(part.content.cloneNode(true));
      } else if (actions) {
        [data?.action, data?.secondary].forEach((action, index) => {
          if (!action?.url || !/^(\/|https:\/\/|tel:)/.test(action.url)) return;
          const link = document.createElement('a');
          link.className = index === 0 ? 'btn' : 'link';
          link.href = action.url;
          if (/^https:/.test(action.url)) {
            link.target = '_blank';
            link.rel = 'noopener';
          }
          link.textContent = action.label || this.strings.go;
          actions.append(link);
        });
        actions.hidden = !actions.children.length;
      }
      this.place(content, data?.intent || 'reply');
    }

    reveal() {
      const rect = this.card.getBoundingClientRect();
      if (rect.bottom > window.innerHeight || rect.top < 0) {
        this.card.scrollIntoView({ block: 'nearest', behavior: window.AmorMotion?.scroll() || 'smooth' });
      }
    }

    hide() {
      window.clearTimeout(this.choosingTimer);
      this.hidden = true;
      this.body.replaceChildren();
    }

    reset() {
      this.token += 1;
      this.answer = null;
      const url = new URL(window.location.href);
      ['q', 'state', 'image', 'r', 'draft'].forEach((key) => url.searchParams.delete(key));
      window.history.pushState({}, '', url);
      this.input.value = '';
      setImage(this.form, null);
      this.hide();
      this.input.focus();
    }
  }

  customElements.define('amor-answer', AmorAnswer);
})();
